import React from 'react';
import { CheckCircle2, Bot, Headphones, RefreshCw, Search, PieChart, Users } from 'lucide-react';

const FEATURE_POINTS = [
  'AI handles repetitive tasks',
  'Humans handle complex decisions',
  'Better efficiency, better results',
];

const NODES = {
  ai: { icon: Bot, title: 'AI Agent', subtitle: 'Automates tasks. Responds instantly' },
  human: { icon: Users, title: 'Human Operator', subtitle: 'Handles complex decisions' },
  support: { icon: Headphones, title: 'Support System', subtitle: 'Directs and handles client requests' },
  crm: { icon: RefreshCw, title: 'CRM', subtitle: 'Keeps customer data in sync' },
  analytics: { icon: Search, title: 'Analytics', subtitle: 'Tracks performance in real time' },
  reporting: { icon: PieChart, title: 'Reporting', subtitle: 'Turns data into clear insights' },
};

const NodeCard = ({ node, accent = false }) => {
  const Icon = node.icon;
  return (
    <div className={`card-tech rounded-2xl p-5 text-center ${accent ? 'border-[#1AB6FF]/60 glow-cyan' : ''}`}>
      <div className="w-14 h-14 mx-auto rounded-full border-2 border-[#1AB6FF]/35 bg-[#1AB6FF]/5 flex items-center justify-center mb-3">
        <Icon size={24} className="text-[#1AB6FF]" />
      </div>
      <p className="font-display font-semibold text-white text-base">{node.title}</p>
      <p className="text-xs text-white/55 mt-1 leading-relaxed">{node.subtitle}</p>
    </div>
  );
};

const DiagramConnections = () => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" preserveAspectRatio="none">
    <defs>
      <linearGradient id="diag-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1AB6FF" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#1AB6FF" stopOpacity="0.15" />
      </linearGradient>
    </defs>
    <line x1="50%" y1="20%" x2="50%" y2="50%" stroke="url(#diag-grad)" strokeWidth="1.5" strokeDasharray="4 4" />
    <line x1="50%" y1="60%" x2="20%" y2="85%" stroke="url(#diag-grad)" strokeWidth="1.5" strokeDasharray="4 4" />
    <line x1="50%" y1="60%" x2="80%" y2="85%" stroke="url(#diag-grad)" strokeWidth="1.5" strokeDasharray="4 4" />
  </svg>
);

const DiagramGrid = () => (
  <div className="relative z-10 space-y-6">
    <div className="grid grid-cols-3 gap-4">
      <div />
      <NodeCard node={NODES.ai} accent />
      <div />
    </div>
    <div className="grid grid-cols-3 gap-4">
      <NodeCard node={NODES.support} />
      <NodeCard node={NODES.human} />
      <NodeCard node={NODES.crm} />
    </div>
    <div className="grid grid-cols-3 gap-4">
      <NodeCard node={NODES.analytics} />
      <div />
      <NodeCard node={NODES.reporting} />
    </div>
  </div>
);

const DiagramVisual = () => (
  <div className="relative">
    <DiagramConnections />
    <DiagramGrid />
  </div>
);

const DiagramCopy = () => (
  <div>
    <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-tight">
      Humans Where Needed. <span className="gradient-text">Automation Where Possible.</span>
    </h2>
    <p className="mt-6 text-white/65 leading-relaxed">
      Geekynd India combines intelligent automation with human expertise to build operational systems
      that scale with your business&mdash;without the typical operational overhead of growing in India.
    </p>
    <ul className="mt-8 space-y-4">
      {FEATURE_POINTS.map((t) => (
        <li key={t} className="flex items-center gap-3">
          <CheckCircle2 size={22} className="text-[#1AB6FF] shrink-0" />
          <span className="text-white/85">{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

const AutomationDiagram = () => (
  <section className="relative py-24 lg:py-32 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
      <DiagramCopy />
      <DiagramVisual />
    </div>
  </section>
);

export default AutomationDiagram;
